import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiUrl } from "@/lib/api";
import { PageHeader, Section, Btn, Modal, Input } from "@/components/Shared";
import { Plus, Upload, Search, FileText, Trash2, Download, AlertTriangle, MessageSquare, Paperclip, ArrowRight, Check, X } from "lucide-react";
import { toast } from "sonner";
import { openConfirm, openPrompt } from "@/lib/dialogs";

// Admin Leads — full CRUD + lifecycle.
//
// Lifecycle: New → Onboarding ⇄ Pending → Approved | Rejected
// Stage-change rules enforced server-side (`PATCH /api/leads/{id}/stage`).
// Approved leads are locked; reopening only happens automatically when the
// converted merchant is later deleted (server.py merchants DELETE → leads.py
// reopen_lead_on_merchant_delete).

const STAGES = ["New", "Onboarding", "Pending", "Approved", "Rejected"];
const STAGE_TONES = {
  New:        "bg-slate-100 text-slate-700 border-slate-200",
  Onboarding: "bg-sky-100 text-sky-800 border-sky-200",
  Pending:    "bg-amber-100 text-amber-800 border-amber-200",
  Approved:   "bg-emerald-100 text-emerald-800 border-emerald-200",
  Rejected:   "bg-rose-100 text-rose-800 border-rose-200",
};

const EMPTY_LEAD = {
  name: "", eligible_lobs: [], contact_number: "", email: "",
  poc_name: "", website: "", existing_volumes: [], notes: "",
};

export default function Leads() {
  const [data, setData] = useState({ rows: [], kpis: { total: 0, by_stage: {}, conversion_pct: 0 } });
  const [loading, setLoading] = useState(true);
  const [filterStage, setFilterStage] = useState("");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [openLead, setOpenLead] = useState(null);  // currently expanded lead
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/leads", { params: filterStage ? { stage: filterStage } : {} });
      setData(payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leads");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterStage]);

  const visible = useMemo(() => {
    if (!q.trim()) return data.rows;
    const ql = q.trim().toLowerCase();
    return data.rows.filter((r) =>
      r.name.toLowerCase().includes(ql)
      || (r.email || "").toLowerCase().includes(ql)
      || (r.poc_name || "").toLowerCase().includes(ql)
      || (r.created_by_partner_name || "").toLowerCase().includes(ql)
    );
  }, [data.rows, q]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: r } = await api.post("/leads/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.inserted} leads`);
      if (r.errors?.length) toast.warning(`${r.errors.length} issues`, { description: r.errors.slice(0, 2).join(" · ") });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    }
    setImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  // Template + export use native-browser downloads: window.open piggybacks on
  // the httponly access_token cookie, mirroring the Partner-portal PDF flow
  // and sidestepping the axios refresh interceptor's blob-response quirks.
  const downloadTemplate = () => {
    window.open(apiUrl("/leads/sample-template"), "_blank");
  };
  const exportLeads = () => {
    const qs = filterStage ? `?stage=${encodeURIComponent(filterStage)}` : "";
    toast.message("Preparing leads export…");
    window.open(apiUrl(`/leads/export${qs}`), "_blank");
  };

  // Inline row delete (used by the trash icon next to ArrowRight) — kept
  // outside the detail panel so admins can prune obvious junk without
  // opening it. Stops row click from also opening the panel.
  const deleteRow = async (e, lead) => {
    e.stopPropagation();
    const ok = await openConfirm({
      title: "Delete this lead?",
      message: `${lead.name} and all its comments / files will be permanently removed. This cannot be undone.`,
      confirmLabel: "Delete lead",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="leads-page">
      <PageHeader
        title="Leads"
        subtitle="Onboarding pipeline — track every prospect from first contact to MID issuance"
        actions={
          <>
            <input
              ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" onChange={onUpload}
              className="hidden" data-testid="leads-file-input"
            />
            <Btn variant="ghost" testid="leads-template-btn" onClick={downloadTemplate}>
              <FileText size={14} /> Template
            </Btn>
            <Btn variant="ghost" testid="leads-import-btn" disabled={importing} onClick={() => fileRef.current?.click()}>
              <Upload size={14} /> {importing ? "Importing…" : "Bulk import"}
            </Btn>
            <Btn variant="ghost" testid="leads-export-btn" onClick={exportLeads}>
              <Download size={14} /> Export
            </Btn>
            <Btn variant="accent" testid="leads-new-btn" onClick={() => setCreating(true)}>
              <Plus size={14} /> Add lead
            </Btn>
          </>
        }
      />
      <div className="p-4 sm:p-10 max-w-6xl space-y-6">
        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 auto-rows-fr" data-testid="leads-kpis">
          <Kpi label="Total leads" value={data.kpis.total} sub="All-time" />
          {STAGES.map((s) => (
            <Kpi
              key={s}
              label={s}
              value={data.kpis.by_stage?.[s] ?? 0}
              sub={s === "Approved" ? "Conversions" : s === "Rejected" ? "PG-rejected" : "In flight"}
              tone={STAGE_TONES[s]}
              onClick={() => setFilterStage((cur) => cur === s ? "" : s)}
              active={filterStage === s}
              testid={`leads-kpi-${s.toLowerCase()}`}
            />
          ))}
        </div>
        <div className="text-[12px] text-[var(--muted)]">
          Conversion rate: <b className="text-[var(--ink)]">{data.kpis.conversion_pct}%</b> (excludes raw "New" inbound)
        </div>

        <Section
          title={`${visible.length} of ${data.rows.length} leads${filterStage ? ` · ${filterStage}` : ""}`}
          right={
            <div className="flex items-center gap-2">
              <div className="relative w-full sm:w-64">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lead / POC / email…" className="pl-8" data-testid="leads-search" />
              </div>
              {filterStage && <Btn variant="ghost" onClick={() => setFilterStage("")}><X size={12} /> Clear</Btn>}
            </div>
          }
        >
          <div className="overflow-auto">
            <table className="table-data w-full">
              <thead>
                <tr>
                  <th>Name</th><th>POC</th><th>LOBs</th><th>Source</th>
                  <th>Stage</th><th>Discrepancies</th><th>Last update</th><th></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-[var(--muted)]">Loading leads…</td></tr>}
                {!loading && visible.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[var(--muted)]">No leads yet — click "Add lead" or "Bulk import" to get started.</td></tr>}
                {!loading && visible.map((r) => {
                  const open = (r.comments || []).filter((c) => c.is_discrepancy && !c.resolved).length;
                  return (
                    <tr key={r.id} className="cursor-pointer hover:bg-[var(--surface-2)]" onClick={() => setOpenLead(r.id)} data-testid={`lead-row-${r.id}`}>
                      <td className="font-medium">
                        {r.name}
                        {r.is_new_mid_for_existing && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">New MID</span>}
                        {r.is_orphan && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200" title="Approved without a merchant row — re-open and convert properly">Orphan</span>}
                      </td>
                      <td className="text-[12px] text-[var(--muted)]">{r.poc_name || r.email || "—"}</td>
                      <td className="text-[11.5px]">{(r.eligible_lobs || []).join(", ") || "—"}</td>
                      <td className="text-[11.5px] text-[var(--muted)]">
                        {r.created_via === "admin" && "Admin"}
                        {r.created_via === "partner" && <>Partner: <b className="text-[var(--ink)]">{r.created_by_partner_name || r.created_by_partner_id}</b></>}
                        {r.created_via === "merchant" && <>Merchant: <b className="text-[var(--ink)]">{r.created_by_merchant_name}</b></>}
                      </td>
                      <td><span className={`text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${STAGE_TONES[r.stage]}`}>{r.stage}</span></td>
                      <td>{open > 0 ? <span className="text-[var(--negative)] text-[12px] font-semibold">{open}</span> : <span className="text-[var(--muted)] text-[12px]">—</span>}</td>
                      <td className="text-[11.5px] text-[var(--muted)]">{new Date(r.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                      <td>
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={(e) => deleteRow(e, r)}
                            className="p-1 text-[var(--negative)] hover:bg-red-50 rounded-sm border border-red-100 hover:border-red-200"
                            title="Delete this lead"
                            data-testid={`lead-row-delete-${r.id}`}
                          ><Trash2 size={12} /></button>
                          <ArrowRight size={13} className="text-[var(--muted)]" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      {/* Create modal */}
      {creating && <CreateLeadModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}

      {/* Detail panel */}
      {openLead && (
        <LeadDetailPanel
          leadId={openLead}
          isAdmin={true}
          onClose={() => { setOpenLead(null); load(); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, sub, tone, onClick, active, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`rise text-left bg-[var(--surface)] border p-4 flex flex-col h-full transition-all ${active ? "border-[var(--ink-2)] ring-2 ring-[var(--accent)]/30" : "border-[var(--line)] hover:border-[var(--line-2)]"} ${onClick ? "cursor-pointer" : "cursor-default"}`}
    >
      <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${tone ? "" : "text-[var(--muted)]"}`}>
        {tone ? <span className={`inline-block px-1.5 py-0.5 rounded-sm border ${tone}`}>{label}</span> : label}
      </div>
      <div className="mt-2 text-[22px] font-semibold text-[var(--ink)]">{value}</div>
      <div className="text-[10.5px] text-[var(--muted)] mt-auto pt-1">{sub}</div>
    </button>
  );
}

function CreateLeadModal({ onClose, onCreated }) {
  const [f, setF] = useState(EMPTY_LEAD);
  const [busy, setBusy] = useState(false);
  const [allLobs, setAllLobs] = useState([]);
  const [selectedLobs, setSelectedLobs] = useState([]);
  useEffect(() => {
    api.get("/meta").then(({ data }) => setAllLobs(data.lobs || [])).catch(() => { /* silent */ });
  }, []);
  const toggleLob = (lob) => {
    setSelectedLobs((cur) => cur.includes(lob) ? cur.filter((l) => l !== lob) : [...cur, lob]);
  };
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    if (selectedLobs.length === 0) { toast.error("Pick at least one LOB"); return; }
    // Phone: strict 10-digit Indian mobile (after stripping +91/0/spaces).
    const cleanedPhone = f.contact_number.replace(/\s|-|\(|\)/g, "").replace(/^(\+91|0)/, "");
    if (!/^[6-9]\d{9}$/.test(cleanedPhone)) {
      toast.error("Contact number must be a 10-digit Indian mobile (starts with 6/7/8/9)");
      return;
    }
    // Email: basic well-formed check (the backend only stores it as a string
    // — strict RFC-compliant validation would reject too many real-world
    // inputs).
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) {
      toast.error("Email must look like name@domain.com");
      return;
    }
    if (!f.poc_name.trim()) { toast.error("POC name required"); return; }
    setBusy(true);
    try {
      await api.post("/leads", { ...f, contact_number: cleanedPhone, eligible_lobs: selectedLobs });
      toast.success("Lead created");
      onCreated?.();
    } catch (e) {
      // Server responds with 409 + a rich message when the name collides
      // with an existing merchant — surface it as a longer toast so the
      // user can read the suggested workflow.
      const detail = e?.response?.data?.detail || "Create failed";
      if (e?.response?.status === 409) {
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error(detail);
      }
    }
    setBusy(false);
  };
  return (
    <Modal open={true} onClose={onClose} title="Add lead" testid="lead-create-modal" size="lg">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Name *" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="lead-form-name" />
        <Field label="POC name *" value={f.poc_name} onChange={(v) => setF({ ...f, poc_name: v })} testid="lead-form-poc" />
        <Field label="Contact number *" value={f.contact_number} onChange={(v) => setF({ ...f, contact_number: v })} testid="lead-form-phone" />
        <Field label="Email *" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="lead-form-email" />
        <div className="sm:col-span-2">
          <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Eligible LOBs *</label>
          <div className="mt-1 px-2 py-2 border border-[var(--line-2)] bg-[var(--surface)] rounded-sm" data-testid="lead-form-lobs">
            {allLobs.length === 0 && <div className="text-[11.5px] text-[var(--muted)] px-1 py-0.5">Loading LOBs…</div>}
            <div className="flex flex-wrap gap-1.5">
              {allLobs.map((lob) => {
                const on = selectedLobs.includes(lob);
                return (
                  <button
                    key={lob}
                    type="button"
                    onClick={() => toggleLob(lob)}
                    className={`text-[11.5px] px-2 py-1 rounded-sm border transition-colors ${on ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "bg-[var(--surface-2)] text-[var(--ink-2)] border-[var(--line-2)] hover:border-[var(--ink-2)]"}`}
                    data-testid={`lead-form-lob-${lob}`}
                  >{on ? "✓ " : ""}{lob}</button>
                );
              })}
            </div>
          </div>
          <div className="text-[10.5px] text-[var(--muted)] mt-1">{selectedLobs.length} selected · click chips to toggle</div>
        </div>
        <Field label="Website" value={f.website} onChange={(v) => setF({ ...f, website: v })} testid="lead-form-website" />
        <div className="sm:col-span-2">
          <Field label="Notes" multiline value={f.notes} onChange={(v) => setF({ ...f, notes: v })} testid="lead-form-notes" />
        </div>
      </div>
      <div className="text-[11.5px] text-[var(--muted)] mt-2 leading-relaxed">
        For website login credentials and existing-PG volumes, use the lead's comment thread after creation — keeps sensitive data scoped to the discussion log.
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="accent" onClick={submit} disabled={busy} testid="lead-form-save">{busy ? "Saving…" : "Add lead"}</Btn>
      </div>
    </Modal>
  );
}

function Field({ label, hint, value, onChange, multiline, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">{label}</label>
      {multiline
        ? <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full px-3 py-2 mt-1 border border-[var(--line-2)] bg-[var(--surface)] rounded-sm text-[13px] outline-none focus:border-[var(--accent)]" data-testid={testid} />
        : <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />}
      {hint && <div className="text-[10.5px] text-[var(--muted)] mt-1">{hint}</div>}
    </div>
  );
}

// ---- Lead detail panel — used by Admin / Partner / Merchant -------------

export function LeadDetailPanel({ leadId, isAdmin, onClose, role }) {
  const [lead, setLead] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draftComment, setDraftComment] = useState("");
  const [isDiscrepancy, setIsDiscrepancy] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/leads/${leadId}`);
      setLead(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load lead");
      onClose?.();
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [leadId]);

  const moveTo = async (newStage) => {
    if (!isAdmin) return;
    let note = "";
    if (newStage === "Rejected") {
      const reason = await openPrompt({
        title: "Reject this lead?",
        message: "This will mark the lead as rejected by the gateway. The reason below is shared with the partner / merchant on their portal.",
        placeholder: "e.g. KYC documents incomplete · ROI not viable · Risk policy mismatch",
        confirmLabel: "Reject lead",
        type: "textarea",
      });
      if (reason === null) return;  // user cancelled
      note = reason;
    }
    setBusy(true);
    try {
      await api.patch(`/leads/${leadId}/stage`, { new_stage: newStage, note });
      toast.success(`Moved to ${newStage}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stage change failed");
    }
    setBusy(false);
  };

  const reopenOrphan = async () => {
    const ok = await openConfirm({
      title: "Re-open this lead?",
      message: "This Approved lead never had a merchant row created. Re-opening moves it back to Pending so you can convert it properly through the Convert-to-merchant flow.",
      confirmLabel: "Re-open lead",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/reopen`);
      toast.success("Re-opened in Pending");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-open failed");
    }
    setBusy(false);
  };

  const deleteLead = async () => {
    const ok = await openConfirm({
      title: "Delete this lead?",
      message: `${lead.name} and all its comments / files will be permanently removed. This cannot be undone.`,
      confirmLabel: "Delete lead",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead deleted");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const addComment = async () => {
    if (!draftComment.trim()) { toast.error("Comment cannot be empty"); return; }
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/comments`, { text: draftComment.trim(), is_discrepancy: isDiscrepancy });
      setDraftComment(""); setIsDiscrepancy(false);
      toast.success(isDiscrepancy ? "Discrepancy raised" : "Comment added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add comment");
    }
    setBusy(false);
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10 MB per file"); return; }
    if ((lead.files || []).length >= 5) { toast.error("Max 5 files per lead — delete one first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/leads/${leadId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const deleteFile = async (fid) => {
    const ok = await openConfirm({ title: "Delete file?", message: "This action cannot be undone.", confirmLabel: "Delete", danger: true });
    if (!ok) return;
    try {
      await api.delete(`/leads/${leadId}/files/${fid}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!lead) {
    return (
      <Modal open={true} onClose={onClose} title="Loading lead…" testid="lead-detail-loading" size="xl">
        <div className="py-12 text-center text-[var(--muted)] text-[12.5px]">Loading…</div>
      </Modal>
    );
  }

  const openDiscrepancies = (lead.comments || []).filter((c) => c.is_discrepancy && !c.resolved);

  return (
    <Modal open={true} onClose={onClose} title={lead.name} testid="lead-detail" size="xl">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: info + lifecycle */}
        <div className="lg:col-span-2 space-y-4">
          {/* Stage strip */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10.5px] uppercase tracking-wider text-[var(--muted)] font-semibold">Stage:</span>
            <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-medium ${STAGE_TONES[lead.stage]}`} data-testid="lead-detail-stage">{lead.stage}</span>
            {isAdmin && lead.stage !== "Approved" && (
              <div className="flex items-center gap-1.5 ml-2 flex-wrap">
                {STAGES.filter((s) => s !== lead.stage && s !== "Approved").map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => moveTo(s)}
                    disabled={busy}
                    className="text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-[var(--line)] hover:bg-[var(--surface-2)] text-[var(--ink-2)]"
                    data-testid={`lead-stage-${s.toLowerCase()}`}
                  >→ {s}</button>
                ))}
              </div>
            )}
            {isAdmin && lead.stage !== "Approved" && (
              <Btn variant="accent" className="ml-auto" onClick={() => setShowConvert(true)} testid="lead-convert-btn">
                <Check size={12} /> Convert to merchant
              </Btn>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={deleteLead}
                disabled={busy}
                className="p-1.5 text-[var(--negative)] hover:bg-red-50 rounded-sm border border-red-200"
                title={lead.stage === "Approved" ? "Delete (only allowed if no merchant exists)" : "Delete this lead"}
                data-testid="lead-delete-btn"
              ><Trash2 size={13} /></button>
            )}
            {lead.stage === "Approved" && lead.approved_merchant_id && !lead.is_orphan && (
              <span className="ml-auto text-[11.5px] text-[var(--muted)]">Merchant: <code className="text-[11px] bg-[var(--surface-2)] px-1 py-0.5 rounded-sm">{lead.approved_merchant_id}</code></span>
            )}
            {isAdmin && lead.stage === "Approved" && lead.is_orphan && (
              <Btn variant="accent" className="ml-auto" onClick={reopenOrphan} disabled={busy} testid="lead-reopen-btn">
                Re-open lead
              </Btn>
            )}
          </div>

          {isAdmin && lead.stage === "Approved" && lead.is_orphan && (
            <div className="p-3 rounded-sm border border-amber-200 bg-amber-50 text-amber-900 text-[12.5px] flex items-start gap-2" data-testid="lead-orphan-banner">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                <b>Orphan Approved lead.</b> This lead is marked Approved but has no corresponding merchant row — likely approved via a stage shortcut before the Convert flow was enforced. Click <b>Re-open lead</b> to move it back to Pending and convert it properly (or use the trash icon to delete it).
              </div>
            </div>
          )}

          {openDiscrepancies.length > 0 && !isAdmin && (
            <div className="p-3 rounded-sm border border-amber-200 bg-amber-50 text-amber-900 text-[12.5px] flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                <b>{openDiscrepancies.length} open discrepanc{openDiscrepancies.length === 1 ? "y" : "ies"}.</b> Please address below — uploading a file with a clarifying comment will mark them resolved and move the lead back to Onboarding.
              </div>
            </div>
          )}

          {/* Quick info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[12.5px]">
            <Info label="POC" value={lead.poc_name} />
            <Info label="Email" value={lead.email} />
            <Info label="Phone" value={lead.contact_number} />
            <Info label="Website" value={lead.website ? <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] underline">{lead.website}</a> : "—"} />
            <Info label="LOBs" value={(lead.eligible_lobs || []).join(", ") || "—"} />
            <Info label="Source" value={
              lead.created_via === "partner" ? `Partner: ${lead.created_by_partner_name || lead.created_by_partner_id}`
              : lead.created_via === "merchant" ? `Merchant: ${lead.created_by_merchant_name}`
              : "Admin"
            } />
            {lead.requested_gateway && <Info label="Requested PG" value={lead.requested_gateway} />}
            {lead.requested_lob && <Info label="Requested LOB" value={lead.requested_lob} />}
            {lead.requested_rate != null && <Info label="Merchant rate" value={`${(lead.requested_rate * 100).toFixed(2)}%`} />}
            {lead.notes && <div className="sm:col-span-2"><Info label="Notes" value={lead.notes} /></div>}
          </div>

          {/* Comments thread */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-2 flex items-center gap-1.5">
              <MessageSquare size={12} /> Discussion ({(lead.comments || []).length})
            </div>
            <div className="space-y-2 max-h-[300px] overflow-auto pr-1">
              {(lead.comments || []).length === 0 && <div className="text-[12px] text-[var(--muted)] italic">No messages yet.</div>}
              {(lead.comments || []).map((c) => (
                <div key={c.id} className={`p-2.5 rounded-sm border text-[12.5px] ${c.is_discrepancy ? (c.resolved ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50") : "border-[var(--line)] bg-[var(--surface-2)]"}`}>
                  <div className="flex items-center gap-2 text-[10.5px] text-[var(--muted)] mb-1">
                    <b className="text-[var(--ink)] uppercase tracking-wider">{c.by_role}</b>
                    <span>{c.by_email}</span>
                    <span>·</span>
                    <span>{new Date(c.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                    {c.is_discrepancy && <span className={`ml-auto px-1.5 py-0 rounded-sm font-bold uppercase tracking-wider ${c.resolved ? "bg-emerald-200 text-emerald-900" : "bg-amber-300 text-amber-950"}`}>{c.resolved ? "Resolved" : "Discrepancy"}</span>}
                  </div>
                  <div className="text-[var(--ink)] whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
            </div>
            {/* Compose */}
            {lead.stage !== "Approved" && (
              <div className="mt-3 space-y-2 border-t border-[var(--line)] pt-3">
                <textarea
                  value={draftComment}
                  onChange={(e) => setDraftComment(e.target.value)}
                  placeholder={isAdmin ? "Add a comment, or check 'Discrepancy' to flag for partner/merchant action…" : "Add a comment or upload a file to clear an open discrepancy…"}
                  rows={3}
                  className="w-full px-3 py-2 border border-[var(--line-2)] bg-[var(--surface)] rounded-sm text-[13px] outline-none focus:border-[var(--accent)]"
                  data-testid="lead-comment-input"
                />
                <div className="flex items-center gap-3 flex-wrap">
                  {isAdmin && (
                    <label className="text-[12px] inline-flex items-center gap-1.5 cursor-pointer select-none">
                      <input type="checkbox" checked={isDiscrepancy} onChange={(e) => setIsDiscrepancy(e.target.checked)} data-testid="lead-comment-disc" />
                      Mark as discrepancy
                    </label>
                  )}
                  <Btn variant="accent" onClick={addComment} disabled={busy || !draftComment.trim()} testid="lead-comment-submit">
                    {busy ? "Posting…" : (isDiscrepancy ? "Raise discrepancy" : "Post comment")}
                  </Btn>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: files + history */}
        <div className="space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-2 flex items-center gap-1.5">
              <Paperclip size={12} /> Files ({(lead.files || []).length}/5)
            </div>
            {(lead.files || []).length === 0 && <div className="text-[11.5px] text-[var(--muted)] italic">No files uploaded.</div>}
            <div className="space-y-1.5">
              {(lead.files || []).map((f) => (
                <div key={f.id} className="flex items-center gap-2 p-1.5 border border-[var(--line)] rounded-sm bg-[var(--surface)] text-[11.5px]">
                  <FileText size={12} className="text-[var(--muted)] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-[var(--ink)] font-medium" title={f.filename}>{f.filename}</div>
                    <div className="text-[10px] text-[var(--muted)]">{Math.ceil(f.size_bytes / 1024)} KB · {new Date(f.at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} · {f.by_role}</div>
                  </div>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/leads/${leadId}/files/${f.id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="p-1 hover:bg-[var(--surface-2)] rounded-sm text-[var(--accent)]"
                    data-testid={`file-download-${f.id}`}
                  ><Download size={11} /></a>
                  <button onClick={() => deleteFile(f.id)} className="p-1 hover:bg-red-50 rounded-sm text-[var(--negative)]" data-testid={`file-delete-${f.id}`}><Trash2 size={11} /></button>
                </div>
              ))}
            </div>
            {lead.stage !== "Approved" && (lead.files || []).length < 5 && (
              <div className="mt-2">
                <input ref={fileRef} type="file" onChange={onUploadFile} className="hidden" data-testid="lead-file-input" />
                <Btn variant="ghost" onClick={() => fileRef.current?.click()} disabled={busy} testid="lead-file-upload-btn">
                  <Upload size={12} /> Upload file
                </Btn>
                <div className="text-[10px] text-[var(--muted)] mt-1">Max 10 MB per file · up to 5 files per lead</div>
              </div>
            )}
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-2">Activity</div>
            <div className="space-y-1.5 text-[11.5px]">
              {(lead.stage_history || []).slice().reverse().map((h, i) => (
                <div key={`${h.at || ""}-${h.stage || ""}-${i}`} className="border-l-2 border-[var(--line-2)] pl-2.5 py-0.5">
                  <div className="text-[var(--ink)]"><b>{h.stage}</b>{h.note ? ` · ${h.note}` : ""}</div>
                  <div className="text-[10.5px] text-[var(--muted)]">{h.by} · {new Date(h.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Convert modal */}
      {showConvert && <ConvertLeadModal lead={lead} onClose={() => setShowConvert(false)} onConverted={() => { setShowConvert(false); load(); }} />}
    </Modal>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider text-[var(--muted)] font-semibold">{label}</div>
      <div className="text-[var(--ink)]">{value || "—"}</div>
    </div>
  );
}

function ConvertLeadModal({ lead, onClose, onConverted }) {
  const [partners, setPartners] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [allLobs, setAllLobs] = useState([]);
  // Rate stored as % in the form (e.g. "1.8" for 1.8%). Stored on the
  // backend as a decimal (0.018) — we convert at submit time.
  const [f, setF] = useState({
    merchant_id: "",
    merchant_name: lead.name,
    gateway: lead.requested_gateway || "",
    lob: lead.requested_lob || (lead.eligible_lobs?.[0] || ""),
    merchant_rate_pct: lead.requested_rate ? (Number(lead.requested_rate) * 100).toFixed(4).replace(/\.?0+$/, "") : "",
    settlement_type: "T+1",
    partner_id: lead.created_by_partner_id || "",
    secondary_partner_id: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/partners"), api.get("/gateways"), api.get("/meta")]).then(([p, g, m]) => {
      setPartners(p.data); setGateways(g.data); setAllLobs(m.data?.lobs || []);
    }).catch(() => { /* silent */ });
  }, []);

  // Merge gateway-specific LOBs (from selected gateway's rate cards) with
  // the global LOB list — gateway-specific ones rendered first since they
  // are the most likely valid choices.
  const lobOptions = useMemo(() => {
    const gw = gateways.find((g) => g.gateway_name === f.gateway);
    const gwLobs = (gw?.lob_rates || []).map((r) => r.lob).filter(Boolean);
    const set = new Set();
    const ordered = [];
    [...gwLobs, ...allLobs, ...(lead.eligible_lobs || [])].forEach((l) => {
      if (l && !set.has(l)) { set.add(l); ordered.push(l); }
    });
    return ordered;
  }, [gateways, f.gateway, allLobs, lead.eligible_lobs]);

  const submit = async () => {
    if (!f.merchant_id.trim()) { toast.error("MID required (PG-issued)"); return; }
    if (!f.gateway || !f.lob) { toast.error("Gateway and LOB required"); return; }
    const ratePct = Number(f.merchant_rate_pct);
    if (!ratePct || ratePct <= 0) { toast.error("Merchant rate required"); return; }
    if (ratePct > 100) { toast.error("Rate cannot exceed 100%"); return; }
    setBusy(true);
    try {
      // Convert % → decimal at the boundary: 1.8 % → 0.018.
      const merchant_rate = Number((ratePct / 100).toFixed(8));
      const { merchant_rate_pct: _drop, ...rest } = f;
      await api.post(`/leads/${lead.id}/convert`, { ...rest, merchant_rate });
      toast.success("Converted! Merchant created and lead approved.");
      onConverted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    }
    setBusy(false);
  };

  return (
    <Modal open={true} onClose={onClose} title={`Convert ${lead.name} to merchant`} testid="lead-convert-modal" size="lg">
      <div className="text-[12px] text-[var(--muted)] mb-3">Fill in PG-issued details. Once converted, the lead is locked and a row is added to your Merchants table. Deleting that merchant will reopen this lead in <b>Pending</b> with an automatic note.</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Merchant ID (PG-issued) *" value={f.merchant_id} onChange={(v) => setF({ ...f, merchant_id: v })} testid="convert-mid" />
        <Field label="Merchant name *" value={f.merchant_name} onChange={(v) => setF({ ...f, merchant_name: v })} testid="convert-mname" />
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Gateway *</label>
          <select value={f.gateway} onChange={(e) => setF({ ...f, gateway: e.target.value, lob: "" })} data-testid="convert-gw" className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]">
            <option value="">— Select —</option>
            {gateways.map((g) => <option key={g.gateway_name} value={g.gateway_name}>{g.gateway_name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">LOB *</label>
          <select value={f.lob} onChange={(e) => setF({ ...f, lob: e.target.value })} data-testid="convert-lob" className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]">
            <option value="">— Select —</option>
            {lobOptions.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <Field label="Merchant rate (%) *" hint="Enter as percentage — e.g. 1.8 for 1.8%" value={f.merchant_rate_pct} onChange={(v) => setF({ ...f, merchant_rate_pct: v })} testid="convert-rate" />
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Settlement *</label>
          <select value={f.settlement_type} onChange={(e) => setF({ ...f, settlement_type: e.target.value })} data-testid="convert-st" className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]">
            <option value="T+1">T+1</option>
            <option value="IS">IS (Instant)</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Partner</label>
          <select value={f.partner_id} onChange={(e) => setF({ ...f, partner_id: e.target.value })} data-testid="convert-partner" className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]">
            <option value="">— None —</option>
            {partners.map((p) => <option key={p.partner_id} value={p.partner_id}>{p.partner_name} ({p.partner_id})</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Secondary partner</label>
          <select value={f.secondary_partner_id} onChange={(e) => setF({ ...f, secondary_partner_id: e.target.value })} data-testid="convert-partner2" className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]">
            <option value="">— None —</option>
            {partners.map((p) => <option key={p.partner_id} value={p.partner_id}>{p.partner_name} ({p.partner_id})</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <Field label="Notes" multiline value={f.notes} onChange={(v) => setF({ ...f, notes: v })} testid="convert-notes" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="accent" onClick={submit} disabled={busy} testid="convert-submit"><Check size={13} /> {busy ? "Converting…" : "Convert"}</Btn>
      </div>
    </Modal>
  );
}
